/** BR-SPW-03-11 — live checklist for Set Up Password (TC-SPW3-26–29). */

export type SpPasswordRuleKey = 'minLength' | 'uppercase' | 'digit' | 'special'

export type SpPasswordRuleChecks = Record<SpPasswordRuleKey, boolean>

/** Mobile-style checklist for BFP-1120 web survey account create. */
export type MobileSpPasswordRuleKey =
  | 'minLength'
  | 'uppercase'
  | 'lowercase'
  | 'digit'
  | 'special'
  | 'noSpaces'
  | 'confirmMatch'

export type MobileSpPasswordRuleChecks = Record<MobileSpPasswordRuleKey, boolean>

/** Required for submit (BR-SPW-03-11 / identity API). */
export const SP_PASSWORD_RULE_REQUIRED: readonly SpPasswordRuleKey[] = [
  'minLength',
  'uppercase',
  'digit',
] as const

/** Figma 5074-12998 — includes optional special-character row for live checklist UI. */
export const SP_PASSWORD_RULE_ORDER: readonly SpPasswordRuleKey[] = [
  'minLength',
  'uppercase',
  'digit',
  'special',
] as const

export const SP_PASSWORD_RULE_LABELS: Record<SpPasswordRuleKey, string> = {
  minLength: 'At least 8 characters',
  uppercase: 'One uppercase letter (A-Z)',
  digit: 'One number (0-9)',
  special: 'One special character (!@#$%)',
}

export function evaluateSpPasswordRules(password: string): SpPasswordRuleChecks {
  return {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  }
}

export function allSpPasswordRulesMet(checks: SpPasswordRuleChecks): boolean {
  return SP_PASSWORD_RULE_REQUIRED.every((key) => checks[key])
}

const MOBILE_SPECIAL = /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;/`~]/

export const MOBILE_SP_PASSWORD_RULE_ORDER: readonly MobileSpPasswordRuleKey[] = [
  'minLength',
  'uppercase',
  'lowercase',
  'digit',
  'special',
  'noSpaces',
  'confirmMatch',
] as const

export const MOBILE_SP_PASSWORD_RULE_LABELS: Record<MobileSpPasswordRuleKey, string> = {
  minLength: 'At least 8 characters',
  uppercase: 'One uppercase letter (A-Z)',
  lowercase: 'One lowercase letter (a-z)',
  digit: 'One number (0-9)',
  special: 'One special character',
  noSpaces: 'No spaces',
  confirmMatch: 'Passwords match',
}

export function evaluateMobileSpPasswordRules(
  password: string,
  confirmPassword: string,
): MobileSpPasswordRuleChecks {
  return {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: MOBILE_SPECIAL.test(password),
    noSpaces: password.length > 0 && !/\s/.test(password),
    confirmMatch:
      password.length > 0 && confirmPassword.length > 0 && password === confirmPassword,
  }
}

export function allMobileSpPasswordRulesMet(checks: MobileSpPasswordRuleChecks): boolean {
  return MOBILE_SP_PASSWORD_RULE_ORDER.every((key) => checks[key])
}
